/* eslint-disable react/jsx-key */
import { ComponentFactory, createContext, h, JSX } from 'preact';
import {
	ContentBannerItem,
	ContentBlockItem,
	ContentButtonItem,
	ContentCommissionsBlockItem,
	ContentContainer,
	ContentExtras,
	ContentFormItem,
	ContentHomePageBlogPostItem,
	ContentHTMLItem,
	ContentImageItem,
	ContentItem,
	ContentItemType,
	ContentItemTypeMap,
	ContentMenuBlockItem,
	ContentProductSetBlockItem,
	isContentContainer,
} from '../../common/data/content.js';
import { asList, Maybe } from '../../common/data.js';
import { ProductionTimeFaqTable, SizeChartFaqTable } from './faq-page.js';
import { CSSProperties, HTMLAttributes, lazy, Suspense, useContext } from 'preact/compat';
import { ensure, formatPrice, isEmptyOrNone, isSome, maybe, num } from '../../common/util.js';
import { ProductCard } from './product-card.js';
import dayjs from 'dayjs';
import { ClassNameArray, classNames } from '../lib/util.js';
import { PublicDomain } from '../../common/data/domains.js';
import { Tier } from '../../common/data/stores.js';
import { Picture } from './image.js';
import { parse as parsePath } from 'path';
import { Email } from '../../common/data/messages.js';
import { getContentUrlPrefix } from '../../common/data/images.js';
import { Loading } from './loading.js';

type Styles = JSX.CSSProperties;

export interface ContentContextData {
	target: ('web' | 'email' | 'html' | 'plaintext' | 'faq' | 'email-campaign' | 'email-trx' | 'email-newsletter')[];
	domain: PublicDomain;
	website: string;
	extras: ContentExtras;
	email?: Maybe<Email>;
	link: (href: string) => string;
	image: (path: string) => string;
	unsubscribeLink?: string;
	staticsSet?: Maybe<Set<string>>;
}
export const defaultContentContext: ContentContextData = {
	target: [],
	domain: {} as any,
	website: '',
	extras: {} as any,
	email: null,
	link: s => s,
	image: s => s,
};
export const ContentContext = createContext<ContentContextData>(defaultContentContext);

export type ContentContextDataInit = Omit<ContentContextData, 'website' | 'link' | 'image' | 'templates'> & Partial<Pick<ContentContextData, 'website' | 'link' | 'image'>> & {
	protocol?: Maybe<string>;
};
export const initContentContext = (context: ContentContextDataInit): ContentContextData => ({
	link: s => s,
	image: s => s,
	...context,
	website: context.website ?? `${context.protocol ?? 'https://'}${context.domain.host}`,
});

const classes = (item: ContentItem, ...extra: ClassNameArray) => classNames(
	'item',
	`--${item.type}`,
	item.style && asList(item.style).map(s => `--${s}`),
	...extra,
);

const mergeCss = (...list: Maybe<Styles>[]) => {
	if (!list.some(s => s && Object.keys(s).length)) return undefined;
	const merged: Styles = {};
	for (const s of list) Object.assign(merged, s);
	return merged;
};

function Div({ item, ...props }: HTMLAttributes<HTMLDivElement> & { item: ContentItem }) {
	const { extras } = useContext(ContentContext);
	return h(
		item.tagName ?? asList(item.style).map(s => extras.styleTagNames[s]).find(isSome) ?? 'div',
		{
			id: item.reference ?? undefined,
			class: props.class ?? classes(item),
			style: props.style ?? mergeCss(item.css),
			...props,
		},
	);
}

function ContentRows({ item, css }: { item: ContentContainer; css?: Styles }) {
	return <Div item={item} id={item.reference ?? undefined} style={mergeCss(css, item.css)}>
		{item.children.map(c => <ContentBlock key={c.id} item={c} />)}
	</Div>;
}

function ContentColumns({ item, css }: { item: ContentContainer; css?: Styles }) {
	const colSum = item.children.reduce((a, child) => a + (child.span || 1), 0);
	const style = {
		'--column-count': colSum,
		...item.css,
		...css,
	};
	return <Div item={item} id={item.reference ?? undefined} class={classes(item, item.style ? null : `item--${item.children.length}-columns`)} style={style}>
		{item.children.map(c => <ContentBlock
			key={c.id}
			item={c}
			css={c.span && c.span > 1 ? { gridColumn: `span ${c.span}` } : {}}
		/>)}
	</Div>;
}

function ContentGrid({ item, css }: { item: ContentContainer; css?: Styles }) {
	return <Div
		item={item}
		style={{ ...item.css, gridTemplateColumns: `repeat(${item.gridColumns}, 1fr)`, ...css }}
	>
		{item.children.map(c => <ContentBlock
			key={c.id}
			item={c}
			css={(c.span || 1) > 1 ? { gridColumn: `auto / span ${c.span}` } : {}}
		/>)}
	</Div>;
}

function ContentHtml({ item, css }: { item: ContentHTMLItem; css?: Styles }) {
	return h(item.tagName || 'div', {
		id: item.reference ?? undefined,
		class: classes(item),
		dangerouslySetInnerHTML: { __html: item.html },
		style: mergeCss(css, item.css),
	});
}

function ContentImage({ item, css }: { item: ContentImageItem; css?: Styles }) {

	const { domain, target } = useContext(ContentContext);
	const isEmail = target.includes('email');

	if (!item.src) return <div />;

	const imgStyle: CSSProperties = {
		...item.innerCSS?.img,
		...(item.width ? { width: item.width } : {}),
		...(item.height ? { height: item.height } : {}),
	};
	if (item.imageWidth && item.imageHeight && !(item.width && item.height)) {
		imgStyle.aspectRatio = (item.imageWidth / item.imageHeight).toFixed(3);
	}

	const src = !isEmail ? item.src : item.src[0] == '/' ? `https://${domain.host}${item.src}` : item.src;
	const img = (isEmail || !item.src.match(/\.(png|jpe?g)$/i)
		? <img alt={item.alt ?? undefined} src={src} style={imgStyle} aria-hidden={item.ariaHidden || undefined}/>
		: <Picture
			alt={item.alt ?? undefined}
			src={src}
			style={imgStyle}
			aria-hidden={item.ariaHidden || undefined}
			loading={item.lazy == false ? 'eager' : 'lazy'}
		/>
	);
	
	return <div class={classes(item, !item.style && '--center')} style={mergeCss(css, item.css)}>
		{item.link ? <a href={item.link} style={item.innerCSS?.a}>{img}</a> : img}
		{item.info && <div className="item__image-info">{item.info}</div>}
	</div>;
}

function ContentBanner({ item, css }: { item: ContentBannerItem; css?: Styles }) {

	const imgStyle = {
		...item.innerCSS?.img,
		...(item.width ? { width: item.width } : {}),
		...(item.height ? { height: item.height } : {}),
	};
	const overlayStyle = {
		...item.innerCSS?.overlay,
		...(item.fontColor ? { color: item.fontColor } : {}),
		textShadow: `${item.textShadowX}px ${item.textShadowY}px ${item.textShadowBlur}px ${item.textShadowColor}`,
	};

	if (!item.src) return <div />;

	item.style = item.style ?? 'center';
	const overlay = item.info ? <div class="item__banner-text" style={overlayStyle}>{item.info}</div> : null;

	return <Div item={item} style={mergeCss(css, item.css)}>
		<div class="item__banner-overlay">
			{item.link ? <a style={item.innerCSS?.a} href={item.link}>
				<img alt={item.alt ?? undefined} src={item.src} style={imgStyle} />
				{overlay}
			</a> : <>
				<img alt={item.alt ?? undefined} src={item.src} style={imgStyle} />
				{overlay}
			</>}
		</div>
	</Div>;
}

function ContentButton({ item, css }: { item: ContentButtonItem; css?: Styles }) {

	const { target, website } = useContext(ContentContext);

	if (target.includes('email')) return <table class={classes(item)} width="100%" {...{ align: 'center' }} style={mergeCss(css, item.css)}>
		<tr>
			<td>
				<table>
					<tr>
						<td class="item__button">
							<a style={item.innerCSS?.a} href={item.link}>{item.info}</a>
						</td>
					</tr>
				</table>
			</td>
		</tr>
	</table>;

	return <Div item={item} style={mergeCss(css, item.css)}>
		<a style={item.innerCSS?.a} class="item__button" href={item.link}>{item.info}</a>
	</Div>;

}

function ContentSizeChart({ item, css }: { item: ContentItem; css?: Styles }) {
	return <Div item={item}>
		<SizeChartFaqTable />
	</Div>;
}

function ContentProdTime({ item, css }: { item: ContentItem; css?: Styles }) {
	return <Div item={item}>
		<ProductionTimeFaqTable />
	</Div>;
}

function ContentBlockBlock({ item, css }: { item: ContentBlockItem; css?: Styles }) {
	const { extras } = useContext(ContentContext);
	const block = item.key ? extras.blocks[item.key] : null;
	if (!block) return '';
	if (block.unwrap && isContentContainer(block.content)) {
		return block.content.children.map(item => <ContentBlock item={item} css={css} />);
	}
	
	return <ContentBlock item={block.content} css={css} />;
	
}

const FormBlockImpl = lazy(() => import('./form-block.js').then(m => m.FormBlock));

function ContentFormBlock({ item, css }: { item: ContentFormItem; css?: Styles }) {
	const { extras } = useContext(ContentContext);
	const form = item.form ? extras.forms[item.form] : null;
	if (!form || typeof window == 'undefined') return '';
	return <Div item={item} style={mergeCss(css, item.css)}>
		<Suspense fallback={<Loading />}>
			<FormBlockImpl
				mode="block"
				form={form}
			/>
		</Suspense>
	</Div>;
}

function ContentProductSetBlock({ item, css }: { item: ContentProductSetBlockItem; css?: Styles }) {

	const { extras, domain } = useContext(ContentContext);

	const filteredProducts = extras.productSets[item.id];
	if (isEmptyOrNone(filteredProducts)) return <></>;

	return <Div item={item} style={mergeCss(css, item.css)}>
		<div className="item__productset-category">
			{filteredProducts.map((p, i) => <div key={p.id} className="item__productset-column"><ProductCard
				product={p}
				domain={domain}
				position={i}
			/></div>)}
		</div>
	</Div>;
}

function ContentMenuBlock({ item, css }: { item: ContentMenuBlockItem; css?: Styles }) {

	const { extras, domain } = useContext(ContentContext);
	const { menus } = extras;

	const menu = menus[item.menuId];
	if (!menu) return <></>;
	const { entries } = menu;
	if (!entries) return <></>;

	if (!item.style || item.style == 'icon-grid') return <Div item={item} class={classes(item, !item.style && '--icon-grid')} >
		{entries.map( e => <a href={`${e.link}`} className="item__icon-grid-category">
			<div className="item__icon-grid-icon" style={{ backgroundImage: `url('/data/menu/${e.image}')` }}></div>
			<div className="item__icon-grid-title">{e.title?.toUpperCase()}</div>
		</a>)}
	</Div>;

	if (item.style == 'footer') {
		const linkMenus = entries.slice(0, 3);
		const buttonMenu = entries[3];
		return <Div item={item}>
			<div className="footer">
				<div className="footer__row">
					<div className="footer__column footer__column--brand">
						<img alt={domain.brand_name} className="footer__logo" src={`${getContentUrlPrefix()}/static/${domain.theme}/logo-footer.svg`}/>
					</div>
					{linkMenus.map( menu => <div className="footer__column footer__column--links">
						{menu.title && <h3 className="footer__column-heading">{menu.title}</h3>}
						{maybe(menu.entries, entries => <ul
							className="footer-navbar"
							aria-label={menu.title}
						>
							{entries.map(submenu => <li className="footer-navbar__item">
								<a href={submenu.link} className="footer-navbar__link">{submenu.title}</a>
							</li>)}
						</ul>)}
					</div>)}
					<div className="footer__column footer__column--buttons">
						{buttonMenu?.entries?.map( submenu => <a href={submenu.link} className="footer__button">{submenu.title}</a>)}
					</div>
				</div>
				<p class="footer__copyright">&copy; {dayjs().format('YYYY')} {domain.brand_name}. All rights reserved.</p>
			</div>
		</Div>;
	}

	return <></>;
}

function ContentHomePageBlogPost({ item, css }: { item: ContentHomePageBlogPostItem; css?: Styles }) {

	const { extras } = useContext(ContentContext);
	const homePageBlogPosts = ensure(extras.homePageBlogPosts);
	const first = homePageBlogPosts[0];
	if (!first) return <></>;

	return <Div item={item} >
		<div className="home-page-blog">
			<div className="home-page-blog-post">
				<div className="home-page-blog-post__title"><a href={`/blog/${first.url}`}>{first.title}</a></div>
				<div className="home-page-blog-post__date-author"><span className="home-page-blog-post__date">{first.posted && dayjs(first.posted).format('MMM DD, YYYY')}</span><span className="home-page-blog-post__author">{first.author}</span></div>
				<div className="home-page-blog-post__abstract">
					{first.abstract && <ContentBlock item={first.abstract} />}
				</div>
			</div>
			<div className="home-page-blog-image">
				<div className="home-page-blog-post__image">{first.image && <a href={`/blog/${first.url}`} aria-hidden={true}> <Picture aria-hidden={true} src={`/data/blogs/${first.image}`} /></a>}</div>
			</div>
		</div>
		<div className="home-page-blog-history">
			{homePageBlogPosts.slice(1).map(post => <div className="home-page-blog-history__post">
				{/* eslint-disable-next-line jsx-a11y/anchor-has-content */}
				<div className="home-page-blog-history__image">{post.image && <a href={`/blog/${post.url}`} aria-hidden={true}><Picture aria-hidden={true} src={`/data/blogs/${parsePath(post.image).name}.600.png`} /></a>}</div>
				<div className="home-page-blog-history__post-title"><a href={`/blog/${post.url}`}>{post.title}</a></div>
				<div className="home-page-blog-history__post-date-author"><span className="home-page-blog-history__post-date">{post.posted && dayjs(post.posted).format('MMM DD, YYYY')}</span><span className="home-page-blog-history__post-author">{post.author}</span></div>
			</div>)}
		</div>
	</Div>;
}

function ContentCommissionsBlock({ item, css }: { item: ContentCommissionsBlockItem; css?: Styles }) {

	const { extras } = useContext(ContentContext);
	const { tiers, groups, categories } = ensure(extras.tiers);

	const getDiscount = (key: string, tier: Tier) => num(tier.discounts[key] ?? tier.default_discount);
	
	return <Div item={item} >
		<div class="dashboard-commissions__wrapper">
			<div class="dashboard-commissions__wrapper--responsive">
				<table class="dashboard-commissions__table">
					<thead>
						{item.variant === 'commissions' && <tr>
							<th>Products</th>
							{tiers.map(it => <th>{it.name}</th>)}
						</tr>}
						{['affiliate', 'sponsorship'].includes(item.variant) && <tr>
							<th colSpan={2}>Tier</th>
							{tiers.map(it => <th>{it.name}</th>)}
						</tr>}
						<tr>
							<th colSpan={['affiliate', 'sponsorship'].includes(item.variant) ? 2 : 1}>Goal amount</th>
							{tiers.map(it => <th>{formatPrice(it.min_rolling_total || 0).split('.')[0]}</th>)}
						</tr>
					</thead>

					<tbody>
						{item.variant === 'commissions' && categories.map(cat => <tr class={'dashboard-commissions__row'}>
							<td><svg width={'1'} height={'1'}></svg>{cat.name}</td>
							{tiers.map(tier => <td>{getDiscount(cat.id, tier)}%</td>)}
						</tr>)}
						{item.variant === 'affiliate' && <>
							<tr className={'dashboard-commissions__row'}>
								<td colSpan={2}><svg width={'1'} height={'1'}></svg>Duration days</td>
								{tiers.map(( tier ) => <td>{tier.shortlink_duration_days}</td>)}
							</tr>
							<tr className={'dashboard-commissions__row'}>
								<td colSpan={2}><svg width={'1'} height={'1'}></svg>Commissions</td>
								{tiers.map(( tier ) => <td>{tier.commissions_model_info?.model.default}%</td>)}
							</tr>
						
							<tr className={'dashboard-commissions__row'}>
								<td colSpan={2}><svg width={'1'} height={'1'}></svg>Discount Given</td>
								{tiers.map(( tier ) => <td>{tier.shortlink_discount_percent}%</td>)}
							</tr>
						</>}
						{item.variant === 'sponsorship' && <tr className={'dashboard-commissions__row'}>
							<td colSpan={2}><svg width={'1'} height={'1'}></svg>Sponsorship</td>
							{tiers.map(( tier ) => <td>{Math.round((tier.gift_card_multiplier - 1) * 100)}%</td>)}
						</tr>}
					</tbody>
				</table>
			</div>
		</div>
	</Div>;
}

const blocks: { [Type in ContentItemType]?: ComponentFactory<{ item: ContentItemTypeMap[Type]; css?: Styles }> } = {

	rows: ContentRows,
	columns: ContentColumns,
	grid: ContentGrid,
	html: ContentHtml,
	image: ContentImage,
	banner: ContentBanner,
	button: ContentButton,
	menu: ContentMenuBlock,
	block: ContentBlockBlock,
	form: ContentFormBlock,
	
	productset: ContentProductSetBlock,
	size_chart: ContentSizeChart,
	prod_time: ContentProdTime,

	homePageBlogPost: ContentHomePageBlogPost,
	commissions: ContentCommissionsBlock,
};

export const getDefinedContentTypes = () => Object.keys(blocks);

export function ContentBlock<T extends ContentItem>({ item, css = {} }: { item: T; css?: Styles }) {
	const Renderer = blocks[item.type] as Maybe<ComponentFactory<{ item: T; css?: Styles }>>;
	if (Renderer) return <Renderer item={item} css={css} />;
	return <div />;
}
